import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StoryRow } from "../components/ListRows";
import { FollowMilestone } from "../components/FollowMilestone";

const Milestone = ({ m, open, onToggle }) => (
  <div className="border-b border-white/10" data-testid={`milestone-block-${m.order}`}>
    <button
      type="button"
      onClick={onToggle}
      data-testid={`milestone-toggle-${m.order}`}
      className="group grid w-full grid-cols-[52px_1fr_auto] items-center gap-4 py-7 text-left transition-colors hover:bg-white/[0.03] md:grid-cols-[76px_minmax(0,2fr)_minmax(0,1fr)_auto] md:gap-8"
    >
      <span className="mono text-base text-maasai md:text-xl">{String(m.order).padStart(2, "0")}</span>
      <span className="min-w-0">
        <span className="block font-display text-xl uppercase leading-tight tracking-tight text-white transition-colors group-hover:text-ochre md:text-3xl">
          {m.title}
        </span>
        <span className="mt-2 block text-sm text-zinc-400">{m.summary}</span>
      </span>
      <span className="hidden flex-col items-start md:flex">
        <span className="text-[0.6rem] font-bold uppercase tracking-[0.2em] text-zinc-400">Funding range</span>
        <span className="mono mt-1.5 text-sm text-ochre">{m.target}</span>
        <span className="mt-2 text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500">
          {m.updates.length} {m.updates.length === 1 ? "update" : "updates"}
        </span>
      </span>
      <ChevronDown
        size={20}
        className={`justify-self-end text-zinc-500 transition-transform duration-300 ${open ? "rotate-180 text-ochre" : ""}`}
      />
    </button>

    {open ? (
      <div className="pb-10" data-testid={`milestone-feed-${m.order}`}>
        <div className="mb-6">
          <FollowMilestone order={m.order} title={m.title} />
        </div>
        <div className="flex flex-wrap items-center gap-4 border-y border-white/10 bg-[#0b0b0b] px-5 py-4 md:hidden">
          <span className="mono text-sm text-ochre">{m.target}</span>
          <span className="text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500">
            {m.updates.length} {m.updates.length === 1 ? "update" : "updates"}
          </span>
        </div>
        {m.updates.length ? (
          m.updates.map((s, i) => <StoryRow key={s.id} story={s} index={i} />)
        ) : (
          <p className="border-t border-white/10 py-8 text-sm text-zinc-500" data-testid={`milestone-empty-${m.order}`}>
            No updates published for this step yet. Subscribe below and we will post progress as it happens.
          </p>
        )}
        {m.sources?.length ? (
          <div className="mt-8 border-t border-white/10 pt-6" data-testid={`milestone-sources-${m.order}`}>
            <p className="overline text-ochre">Sources for this step</p>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
              {m.sources.map((s, i) => (
                <a
                  key={s.url + i}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`milestone-source-${m.order}-${i}`}
                  className="text-xs text-ochre underline underline-offset-4 hover:text-white"
                >
                  {s.label} <span className="mono text-zinc-500">{s.publisher}</span>
                </a>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    ) : null}
  </div>
);

export default function MilestoneUpdates() {
  const [data, setData] = useState({ milestones: [], unassigned: [] });
  const [open, setOpen] = useState(null);
  useSeo({
    title: "Football Development Updates",
    description:
      "A running progress feed for each recommended step — futsal courts, headquarters, training facility, academies, women's football and community stadiums.",
  });

  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/milestone-updates")
      .then((r) => {
        setData(r.data);
        const first = r.data.milestones.find((m) => m.updates.length);
        setOpen(first ? first.order : r.data.milestones[0]?.order ?? null);
      })
      .catch(() => setError("We could not load the updates feed. Please refresh."));
  }, []);

  const total = data.milestones.reduce((n, m) => n + m.updates.length, 0);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="updates-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Football development updates</p>
          <h1 className="display-1 mt-5 max-w-3xl">Football Development Updates</h1>
          <p className="body-copy mt-6 max-w-2xl">
            Every recommended step has its own running feed. Open a step to read what has actually moved — surveys, agreements,
            design briefs, feasibility work — alongside the funding range it needs. The steps and their costs are
            research estimates, so expect scope and sequence to change as work progresses.
          </p>
          <div className="mono mt-8 flex flex-wrap gap-8 border-t border-white/10 pt-6 text-sm">
            <span data-testid="updates-count-milestones">
              <span className="text-ochre">{data.milestones.length}</span> recommended steps
            </span>
            <span data-testid="updates-count-updates">
              <span className="text-ochre">{total}</span> updates published
            </span>
            <Link to="/plans" className="text-zinc-400 underline decoration-maasai underline-offset-4 hover:text-white">
              The recommended steps
            </Link>
            <Link to="/news" className="text-zinc-400 underline decoration-maasai underline-offset-4 hover:text-white">
              Everything else in the newsroom
            </Link>
            <Link to="/apply" className="text-zinc-400 underline decoration-maasai underline-offset-4 hover:text-white">
              Send a proposal
            </Link>
          </div>
        </Reveal>

        {error ? (
          <p className="mt-12 border border-maasai/40 bg-maasai/10 p-5 text-sm text-white" data-testid="updates-error">
            {error}
          </p>
        ) : null}

        <div className="mt-14 border-t border-white/20" data-testid="milestone-list">
          {data.milestones.map((m) => (
            <Milestone key={m.id} m={m} open={open === m.order} onToggle={() => setOpen(open === m.order ? null : m.order)} />
          ))}
        </div>

        {data.unassigned.length && false ? null : null}
      </div>
    </div>
  );
}
